import React, { useContext, useEffect, useState, useMemo } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArticleIcon from '@mui/icons-material/Article';
import { CONTEXTS, CONTROLLERS, SERVICES } from 'figshelf-modules';

const WikiArticleApproval = () => {
  const apiService = useContext(CONTEXTS.APIContext);
  const auth = useContext(CONTEXTS.AuthContext);
  const wikiController = useMemo(() => new CONTROLLERS.WikiController(apiService), [apiService]);

  const [pendingArticles, setPendingArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchPendingArticles();
  }, []);

  const fetchPendingArticles = async () => {
    try {
      setIsLoading(true);

      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'wiki_pending',
        params: { status: 'pending' },
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 60 * 1000 });
      if (Array.isArray(cached?.articles)) {
        setPendingArticles(cached.articles);
      }

      const response = await wikiController.getPendingWikiArticles();
      
      if (response.statusCode === 200) {
        setPendingArticles(response.data.data || []);

        await SERVICES.cacheAdminList(
          cacheKey,
          { articles: response.data.data || [] },
          { scope: 'wiki_pending' },
        );
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to load pending articles',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching pending articles:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while loading articles',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewArticle = (article) => {
    setSelectedArticle(article);
    setViewDialogOpen(true);
  };

  const handleApprove = async (articleId) => {
    try {
      const response = await wikiController.approveWikiArticle(articleId);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Article approved successfully',
          severity: 'success'
        });
        fetchPendingArticles();
        setViewDialogOpen(false);
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to approve article',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error approving article:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while approving',
        severity: 'error'
      });
    }
  };

  const handleRejectClick = (article) => {
    setSelectedArticle(article);
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide a rejection reason',
        severity: 'warning'
      });
      return;
    }

    try {
      const response = await wikiController.rejectWikiArticle(selectedArticle.id, rejectionReason);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Article rejected successfully',
          severity: 'success'
        });
        fetchPendingArticles();
        setRejectDialogOpen(false);
        setViewDialogOpen(false);
        setRejectionReason('');
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to reject article',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error rejecting article:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while rejecting',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getCategoryColor = (category) => {
    const colors = {
      'figure-care': '#1E90FF',
      'stores': '#FF7F50',
      'terminology': '#8A2BE2',
      'brands': '#FF6347',
      'customization': '#20B2AA',
      'safety': '#FF4500',
      'photography': '#DAA520',
      '3d-printing': '#00BFFF',
      'events': '#FF69B4',
    };
    return colors[category] || '#666';
  };

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <ArticleIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Wiki Article Approval
          </Typography>
        </Box>

        <Card>
          <CardContent>
            {isLoading && pendingArticles.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : pendingArticles.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No pending articles to review
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Title</strong></TableCell>
                      <TableCell><strong>Author</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell><strong>Submitted</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingArticles.map((article) => (
                      <TableRow key={article.id} hover>
                        <TableCell>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {article.title}
                          </Typography>
                          {article.excerpt && (
                            <Typography variant="caption" color="text.secondary">
                              {article.excerpt.substring(0, 100)}...
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {article.author?.profile?.displayName || article.author?.username || 'Unknown'}
                        </TableCell>
                        <TableCell>
                          {article.category && (
                            <Chip
                              label={article.category}
                              size="small"
                              sx={{
                                backgroundColor: getCategoryColor(article.category),
                                color: 'white',
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(article.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewArticle(article)}
                              sx={{ mr: 1 }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApprove(article.id)}
                              sx={{ mr: 1 }}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRejectClick(article)}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* View Article Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Review Article</Typography>
              {selectedArticle?.category && (
                <Chip
                  label={selectedArticle.category}
                  size="small"
                  sx={{
                    backgroundColor: getCategoryColor(selectedArticle.category),
                    color: 'white',
                  }}
                />
              )}
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedArticle && (
              <Box>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedArticle.title}
                </Typography>

                {selectedArticle.excerpt && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Excerpt:</strong> {selectedArticle.excerpt}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Author:</strong> {selectedArticle.author?.profile?.displayName || selectedArticle.author?.username}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Submitted:</strong> {new Date(selectedArticle.createdAt).toLocaleString()}
                  </Typography>
                  {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>Tags:</strong>
                      </Typography>
                      {selectedArticle.tags.map((tag, index) => (
                        <Chip key={index} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    maxHeight: 400,
                    overflow: 'auto',
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => {
                setRejectDialogOpen(true);
              }}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleApprove(selectedArticle.id)}
            >
              Approve
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog
          open={rejectDialogOpen}
          onClose={() => setRejectDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reject Article</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Please provide a reason for rejecting this article. The author will be notified.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Explain why this article is being rejected..."
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setRejectDialogOpen(false);
              setRejectionReason('');
            }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleRejectConfirm}
              disabled={!rejectionReason.trim()}
            >
              Confirm Rejection
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  );
};

export default WikiArticleApproval;
